import { maybeSignedIn } from '../utils/session';
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const EntitlementContext = createContext();

/**
 * Entitlement store provider.
 * Manages tenant, user, and entitlements state.
 */
export const EntitlementProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [planStatus, setPlanStatus] = useState(null);
  const [subscriptionUpgradeRequired, setSubscriptionUpgradeRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch current user with tenant and entitlements.
   */
  const fetchCurrentUser = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if token exists
      // Optimistic: the server refuses if there is no session (MP-MIG-001).
      if (!maybeSignedIn()) {
        setLoading(false);
        return null;
      }
      
      const response = await api.get('/core/me');
      const data = response.data;
      
      setUser(data.user);
      setTenant(data.tenant);
      setEntitlements(data.entitlements || []);
      setFeatureFlags(data.feature_flags || {});
      setPlanStatus(data.plan_status || null);
      setSubscriptionUpgradeRequired(data.subscription_upgrade_required || false);

      return data;
    } catch (err) {
      // If 401 and no refresh token, clear state
      if (err.response?.status === 401) {
        // Unconditional now. There is no refresh token in the browser to wait
        // on — the refresh token lives in the server-side session that just
        // ended, so a 401 is final here and `api.js` sends them to /auth/login.
        clearState();
      }
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to fetch user data');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh entitlements (e.g., after enabling a module).
   */
  const refreshEntitlements = async () => {
    await fetchCurrentUser();
  };

  /**
   * Check if a module is enabled.
   */
  const hasEntitlement = (module) => {
    return entitlements.includes(module);
  };

  /**
   * Check if a feature flag is enabled.
   */
  const hasFeature = (feature) => {
    return featureFlags[feature] === true;
  };

  /**
   * Clear all state (on logout).
   */
  const clearState = () => {
    setUser(null);
    setTenant(null);
    setEntitlements([]);
    setFeatureFlags({});
    setPlanStatus(null);
    setSubscriptionUpgradeRequired(false);
    setError(null);
  };

  // Ask the server on mount, always (MP-MIG-001).
  //
  // This used to be gated on `localStorage.getItem('access_token')`, which
  // under the BFF is never set — the cookie is HttpOnly. So the gate meant
  // `fetchCurrentUser` never ran, `user` stayed null, and every protected
  // route bounced to /login no matter how well the login had worked.
  //
  // There is nothing to gate on now: `fetchCurrentUser` is already optimistic
  // via `maybeSignedIn()` and treats a 401 as signed out, so the unauthenticated
  // case costs one cheap request that answers the question honestly.
  useEffect(() => {
    fetchCurrentUser().catch(() => {
      // Swallowed deliberately: a 401 here is the normal signed-out path and
      // the state is already cleared. Letting it escape an effect would log an
      // unhandled rejection on every visit by a signed-out visitor.
    });
  }, []);

  const value = {
    user,
    tenant,
    entitlements,
    featureFlags,
    planStatus,
    subscriptionUpgradeRequired,
    loading,
    error,
    fetchCurrentUser,
    refreshEntitlements,
    hasEntitlement,
    hasFeature,
    clearState,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

/**
 * Hook to access entitlement store.
 */
export const useEntitlements = () => {
  const context = useContext(EntitlementContext);
  if (!context) {
    throw new Error('useEntitlements must be used within EntitlementProvider');
  }
  return context;
};

